import { fabric } from 'fabric';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';

import { useQueryLocalEditor } from 'gql/queries/local';
import loadSlide from 'questions/Shared/SlideBuilder/helpers/loadSlide';

import DEFAULT_CANVAS_PROPERTIES from '../../Shared/SlideBuilder/data/defaultCanvasProperties';

import Recto from './Recto';

const BREAK_POINTS = {
  preview: {
    small: 800,
    medium: 1220,
  },
  recto: {
    small: 950,
  },
};

const LARGEST_CANVAS_WIDTH = 920;

const getAvailableHeight = () => {
  const header = document.getElementsByTagName('header')[0];
  const headerStyle = header.currentStyle || window.getComputedStyle(header);
  const footer = document.getElementById('self-assess-footer');
  const canvas = document.getElementsByTagName('canvas')[0];
  const canvasStyle = canvas.currentStyle || window.getComputedStyle(canvas);
  const headerHeight = header.offsetHeight;
  const headerMarginBottom = parseInt(headerStyle.marginBottom);
  const slidePadding = parseInt(canvasStyle.paddingTop) * 2;
  const footerMarginTop = 50;
  const footerHeight = footer.offsetHeight;
  const availableHeight =
    window.innerHeight -
    headerHeight -
    headerMarginBottom -
    slidePadding -
    footerMarginTop -
    footerHeight;
  return availableHeight;
};

const RectoContainer = ({ question, ...props }) => {
  const fabricRef = useRef(null);
  const canvasRef = useRef(null);
  const {
    data: {
      editor: { status: editorStatus },
    },
  } = useQueryLocalEditor();
  const resizeCanvas = useCallback(async () => {
    if (fabricRef?.current) {
      const isPreview = editorStatus === 'question-preview';
      // Resize regarding the window width
      let newCanvasWidth = isPreview
        ? window.innerWidth < BREAK_POINTS.preview.small
          ? window.innerWidth * DEFAULT_CANVAS_PROPERTIES.rectoSmallRatio
          : window.innerWidth < BREAK_POINTS.preview.medium
          ? window.innerWidth * DEFAULT_CANVAS_PROPERTIES.rectoMediumRatio
          : LARGEST_CANVAS_WIDTH
        : window.innerWidth < BREAK_POINTS.recto.small
        ? window.innerWidth * DEFAULT_CANVAS_PROPERTIES.rectoSmallRatio
        : LARGEST_CANVAS_WIDTH;
      let scale = DEFAULT_CANVAS_PROPERTIES.width / newCanvasWidth;
      let zoom = DEFAULT_CANVAS_PROPERTIES.zoom / scale;
      let newCanvasHeight =
        newCanvasWidth / DEFAULT_CANVAS_PROPERTIES.widthHeightRatio;
      // Resize regarding the window height if needed
      if (!isPreview && newCanvasHeight > getAvailableHeight()) {
        newCanvasHeight = getAvailableHeight();
        newCanvasWidth =
          newCanvasHeight * DEFAULT_CANVAS_PROPERTIES.widthHeightRatio;
        scale = DEFAULT_CANVAS_PROPERTIES.height / newCanvasHeight;
        zoom = DEFAULT_CANVAS_PROPERTIES.zoom / scale;
      }
      fabricRef.current.setDimensions({
        width: newCanvasWidth,
        height: newCanvasHeight,
      });
      fabricRef.current.setZoom(zoom);
    }
  }, [editorStatus, fabricRef]);
  useEffect(() => {
    const initFabric = () => {
      fabricRef.current = new fabric.StaticCanvas(canvasRef.current);
    };
    const disposeFabric = () => {
      fabricRef.current.dispose();
    };
    initFabric();
    loadSlide(fabricRef, question.content.slideContent);
    resizeCanvas();
    return () => {
      disposeFabric();
    };
  }, [canvasRef, fabricRef, question, resizeCanvas]);
  useEffect(() => {
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);
  return <Recto canvasRef={canvasRef} {...props} />;
};
RectoContainer.propTypes = {
  question: PropTypes.object.isRequired,
};

RectoContainer.defaultAnswer = () => undefined;

RectoContainer.validationSchema = () => undefined;

export default RectoContainer;
